import { useState, type FormEvent } from 'react';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { useSeo } from '@/hooks/useSeo';
import { FORM_ENDPOINT, SITE } from '@/constants/site';

type Status = 'idle' | 'submitting' | 'success' | 'error';

export default function Contact() {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');

  useSeo({
    title: '联系我们 - 线索投稿与内容纠错',
    description:
      '通过瓜田观察联系我们：提交话题线索、指出内容错误、沟通版权与隐私处理，或提出合作建议。留下你的联系方式，我们会在核实后回复。',
    keywords: '联系我们,线索投稿,内容纠错,版权沟通,隐私处理,联系方式,瓜田观察',
    path: '/contact',
    type: 'website',
    jsonLd: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'ContactPage',
          name: '联系我们',
          description: '提交话题线索、内容纠错与版权沟通。',
          inLanguage: 'zh-CN',
        },
        {
          '@type': 'Organization',
          name: SITE.name,
          email: SITE.email,
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: `${window.location.origin}/` },
            { '@type': 'ListItem', position: 2, name: '联系我们', item: `${window.location.origin}/contact` },
          ],
        },
      ],
    },
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      setFormError('');
      return;
    }

    setStatus('submitting');
    setFormError('');

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') return;
      if (typeof value === 'string' && value.trim() === '') return;
      payload.append(key, value as string);
    });

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isOk = response.ok && (!parsed || parsed?.code === 'OK');

      if (isOk) {
        setStatus('success');
        setFormError('');
        form.reset();
      } else {
        setStatus('error');
        setFormError(String(serverMsg) || '提交失败，请稍后再试。');
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，提交失败，请稍后再试。');
    }
  };

  return (
    <SiteLayout>
      <section className="border-b border-background-200 bg-background-100">
        <div className="w-full px-4 py-10 md:px-8 md:py-14">
          <Breadcrumb items={[{ label: '联系我们' }]} />
          <h1 className="mt-4 font-heading text-3xl font-black text-foreground-950 md:text-4xl">联系我们</h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            有线索、想纠错，或需要沟通版权与隐私问题？填写下面的表单，或直接发邮件给我们。
          </p>
        </div>
      </section>

      <section className="bg-background-50">
        <div className="w-full px-4 py-12 md:px-8 md:py-14">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <div className="border border-background-200 bg-background-100 p-6">
                <h2 className="font-heading text-base font-bold text-foreground-950">直接联系</h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                  邮件是我们处理线索与纠错的主要方式，通常会在两个工作日内回复。
                </p>
                <p className="mt-4 flex items-center gap-2 text-sm text-foreground-700">
                  <i className="ri-mail-line text-primary-500" aria-hidden="true" />
                  <a href={`mailto:${SITE.email}`} className="text-primary-600 underline underline-offset-4">
                    {SITE.email}
                  </a>
                </p>
              </div>

              <div className="mt-5 border border-background-200 bg-background-100 p-6">
                <h2 className="font-heading text-base font-bold text-foreground-950">投稿小贴士</h2>
                <ul className="mt-3 space-y-2.5 text-sm leading-relaxed text-foreground-600">
                  <li className="flex gap-2">
                    <i className="ri-checkbox-blank-circle-fill mt-1.5 text-[6px] text-primary-400" aria-hidden="true" />
                    尽量附上公开可核实的来源链接
                  </li>
                  <li className="flex gap-2">
                    <i className="ri-checkbox-blank-circle-fill mt-1.5 text-[6px] text-primary-400" aria-hidden="true" />
                    标明信息发生的时间，方便我们对时间线
                  </li>
                  <li className="flex gap-2">
                    <i className="ri-checkbox-blank-circle-fill mt-1.5 text-[6px] text-primary-400" aria-hidden="true" />
                    涉及个人隐私的内容请先脱敏再提交
                  </li>
                </ul>
              </div>
            </div>

            <div className="lg:col-span-8">
              <div className="border border-background-200 bg-background-100 p-6 md:p-8">
                <h2 className="font-heading text-lg font-bold text-foreground-950">留下你的信息</h2>

                {status === 'success' && (
                  <div className="mt-5 flex items-start gap-3 border border-secondary-300 bg-secondary-100 p-4 text-sm text-secondary-900">
                    <i className="ri-checkbox-circle-line mt-0.5 text-lg" aria-hidden="true" />
                    提交成功，感谢你的信息，我们会尽快核实并回复。
                  </div>
                )}
                {status === 'error' && (
                  <div className="mt-5 flex items-start gap-3 border border-primary-300 bg-primary-100 p-4 text-sm text-primary-800">
                    <i className="ri-error-warning-line mt-0.5 text-lg" aria-hidden="true" />
                    {formError}
                  </div>
                )}

                <form
                  data-readdy-form
                  id="contact-form"
                  action={FORM_ENDPOINT}
                  method="post"
                  onSubmit={handleSubmit}
                  className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2"
                >
                  <div className="flex flex-col gap-2">
                    <label htmlFor="name" className="text-sm font-medium text-foreground-700">
                      你的称呼 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      maxLength={40}
                      placeholder="怎么称呼你"
                      className="h-11 border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label htmlFor="email" className="text-sm font-medium text-foreground-700">
                      联系邮箱 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      maxLength={80}
                      placeholder="name@example.com"
                      className="h-11 border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label htmlFor="subject_type" className="text-sm font-medium text-foreground-700">
                      联系类型
                    </label>
                    <select
                      id="subject_type"
                      name="subject_type"
                      defaultValue="topic"
                      className="h-11 cursor-pointer border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
                    >
                      <option value="topic">话题线索投稿</option>
                      <option value="correction">内容纠错</option>
                      <option value="copyright">版权与隐私</option>
                      <option value="cooperation">合作建议</option>
                    </select>
                  </div>

                  <div className="flex flex-col gap-2">
                    <label htmlFor="topic_link" className="text-sm font-medium text-foreground-700">
                      相关话题链接（选填）
                    </label>
                    <input
                      id="topic_link"
                      name="topic_link"
                      type="text"
                      maxLength={200}
                      placeholder="贴一个话题页地址，方便我们定位"
                      className="h-11 border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                    />
                  </div>

                  <div className="flex flex-col gap-2 md:col-span-2">
                    <label htmlFor="message" className="text-sm font-medium text-foreground-700">
                      具体内容 <span className="text-primary-500">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      maxLength={500}
                      rows={6}
                      placeholder="请描述你的线索、纠错点或诉求，并尽量附上可核实的公开来源。"
                      className="resize-none border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-relaxed text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                    />
                    <span className="text-xs text-foreground-400">最多 500 字</span>
                  </div>

                  <input
                    className="contact-extra-field"
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  <div className="md:col-span-2">
                    <button
                      type="submit"
                      disabled={status === 'submitting'}
                      className="w-full whitespace-nowrap bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 md:w-auto"
                    >
                      {status === 'submitting' ? '提交中…' : '提交信息'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}